import {
  BUTTON_SELECTED_REPORT_FORMAT,
  convertEventSelectToTarget,
  FormFeedBack,
  PageWrapper,
  Select,
} from '@repo/ui';
import { useFormik } from 'formik';
import {
  initialValuesTeamReport,
  validationSchemaTeamReport,
} from 'presentation/app/common/validation-schema/reports/schema-team-report';
import { ReportsGeneralFilters } from 'presentation/app/modules/Reports/components/reports-general-filters';
import { useTeamReport } from 'presentation/app/modules/Reports/hooks/useTeamReport';
import { useRef } from 'react';
import { Card, Col, Label, Spinner } from 'reactstrap';

import { EmptyTeamState } from './components/empty-team-state';
import { TeamList } from './components/team-list';
import { TeamListSkeleton } from './components/team-list-skeleton';
import { ROLE_OPTIONS } from './helpers/role-options';

type ReportFormat = 'pdf' | 'excel' | 'json';

export const TeamReport = () => {
  const { getTeamReport, handleTeamReport } = useTeamReport();

  const selectedBtnRef = useRef<ReportFormat>('json');

  const professionals = getTeamReport.data;
  const isConsulting = getTeamReport.loading;
  const isExporting = handleTeamReport.loading;

  const isPdfType = selectedBtnRef.current === 'pdf';
  const isExcelType = selectedBtnRef.current === 'excel';

  const disableActions = isConsulting || isExporting;

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: initialValuesTeamReport,
    validationSchema: validationSchemaTeamReport,

    onSubmit: (values) => {
      const valuesToSubmit = {
        entityHashId: values.entity?.value,
        courseHashId: values.course?.value,
        curricularUnitHashId: values.unit?.value,
        semesterHashId: values.semester?.value,
        groupHashIds: values.groups.length
          ? values.groups.map((group) => group.value)
          : undefined,
        positionId: values.position?.value,
      };

      const selectedFormat = selectedBtnRef.current;

      if (selectedFormat === 'json') {
        getTeamReport.request(valuesToSubmit);
        return;
      }

      handleTeamReport.request({
        ...valuesToSubmit,
        type: selectedFormat,
      });
    },
  });

  return (
    <PageWrapper title="Profissionais">
      <Col className="mb-3 border-0 d-flex align-items-end justify-content-between">
        <h4>Profissionais</h4>
      </Col>

      <Card>
        <ReportsGeneralFilters
          validation={validation}
          buttonsChildren={
            <div className="d-flex gap-2 justify-content-end align-content-end">
              <button
                id="search"
                type="submit"
                onClick={() => {
                  selectedBtnRef.current = BUTTON_SELECTED_REPORT_FORMAT.JSON;
                }}
                className="btn btn-primary waves-effect waves-light"
                disabled={disableActions}
              >
                {isConsulting ? (
                  <Spinner size="sm">{['Carregando...']}</Spinner>
                ) : (
                  <>
                    Visualizar
                    <i className="ri-computer-line mx-2"></i>
                  </>
                )}
              </button>

              <button
                type="submit"
                id="export-pdf"
                onClick={() => {
                  selectedBtnRef.current = BUTTON_SELECTED_REPORT_FORMAT.PDF;
                }}
                className="btn btn-light waves-effect waves-light"
                disabled={disableActions}
              >
                {isExporting && isPdfType ? (
                  <Spinner size="sm">{['Carregando...']}</Spinner>
                ) : (
                  <>
                    Visualizar em PDF
                    <i className="las la-file-pdf mx-2"></i>
                  </>
                )}
              </button>
              <button
                id="export-excel"
                type="submit"
                onClick={() => {
                  selectedBtnRef.current = BUTTON_SELECTED_REPORT_FORMAT.EXCEL;
                }}
                className="btn btn-light waves-effect waves-light"
                disabled={disableActions}
              >
                {isExporting && isExcelType ? (
                  <Spinner size="sm">{['Carregando...']}</Spinner>
                ) : (
                  <>
                    Exportar para Excel
                    <i className="ri-file-excel-2-line mx-2"></i>
                  </>
                )}
              </button>
            </div>
          }
          inputsChildren={
            <Col lg={4} className="mb-3">
              <Label htmlFor="final-date" className="form-label">
                Cargo:
              </Label>
              <Select
                isClearable
                id="position"
                name="position"
                placeholder="Cargo"
                value={validation.values.position}
                onChange={(e) => {
                  validation.handleChange(
                    convertEventSelectToTarget('position', e),
                  );
                }}
                onBlur={validation.handleBlur}
                options={ROLE_OPTIONS}
                invalid={
                  !!(validation?.touched && validation?.errors?.position)
                }
              />
              <FormFeedBack
                error={validation?.errors?.position?.toString()}
                isInvalid={
                  !!(validation?.touched && validation?.errors?.position)
                }
              />
            </Col>
          }
        />
      </Card>

      {professionals.length === 0 && !isConsulting && <EmptyTeamState />}

      {isConsulting && <TeamListSkeleton />}

      {!isConsulting && professionals.length > 0 && (
        <TeamList professionals={professionals || []} />
      )}
    </PageWrapper>
  );
};
